import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Lane } from '@bssh/ng-sdk';
import { EMPTY, forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { isNullOrUndefined } from 'util';
import { LaneStatus } from '../../core/model/runs/lane-status';
import { IRun } from '../../core/model/runs/run';
import { IBulkUpdateFailure, IBulkUpdateResponse } from '../../core/model/v2-api/v2-api-wrappers';
import { BsApiEndPoints } from '../../core/services/bs-api/endpoints';
import { ResourceBaseService } from '../../core/services/resource-base.service';

export interface IChangeLaneQcService {
  canChangeLaneQc(lanes: Lane[], run: IRun, userId: string): boolean;
  changeLaneQc(lanes: Lane[], laneStatus: LaneStatus, comments?: string): Observable<IBulkUpdateResponse<Lane>>;
}

@Injectable({
  providedIn: 'root'
})
export class ChangeLaneQcService extends ResourceBaseService<Lane> implements IChangeLaneQcService {

  constructor(private httpClient: HttpClient) {
    super(httpClient, BsApiEndPoints.LANES_URL);
  }

  /**
   * Checks if the change lane qc featured is allowed on the run.
   * @param run The run for which the check is to be made.
   * @param userId User Id of the logged in user.
   */
  public canChangeLaneQc(lanes: Lane[], run: IRun, userId: string): boolean {
    return !isNullOrUndefined(lanes) && lanes.length > 0 && !isNullOrUndefined(run) && !isNullOrUndefined(userId)
      && run.UserOwnedBy.Id === userId && !run.IsArchived;
  }

  /**
   * Changes the lane QC of the lane whose laneId is passed with laneStatus mentioned.
   * @param lanes The array of lanes for which the QC has to be changed.
   * @param laneStatus The status to which the QC has to be changed to.
   * @param comments Additional comments on changing the QC for the lane in question.
   */
  public changeLaneQc(lanes: Lane[], laneStatus: LaneStatus, comments?: string): Observable<IBulkUpdateResponse<Lane>> {
    const laneResponse: IBulkUpdateResponse<Lane> = {
      SuccessItems: [],
      TotalSuccessCount: 0,
      FailureItems: [],
      TotalFailureCount: 0
    };

    const postData = {
      Status: LaneStatus[laneStatus].toString(),
      ...!isNullOrUndefined(comments) && {Comment: comments}
    };

    return forkJoin(
      lanes.map((lane: Lane, index: number) => {
        return this.post(lane.Id, postData).
          pipe(
            catchError(error => {
              const failureObject: IBulkUpdateFailure<Lane> = {
                Id: lane.Id,
                ErrorMessage: error.message,
                ErrorCode: error.status
              };
              laneResponse.FailureItems.push(failureObject);
              laneResponse.TotalFailureCount++;
              return of(undefined);
            }),
          ).pipe(
            map((response) => {
              if (response) {
                laneResponse.SuccessItems.push(response);
                laneResponse.TotalSuccessCount++;
              }
              return EMPTY; // as nothing needs to be returned from this observable, but laneResponse needs to returned
            })
          );
      })
    ).pipe(map((response: any) => {
      return laneResponse;
    }));
  }

}
